import { formatPercent } from "@/utils/format";
import * as echarts from "echarts";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useHeaderNav } from "@/hooks/useHeaderNav";

interface Props {
  isDesktop: boolean;
  // 新增：可直接传入通用序列
  xLabels?: string[];
  series?: Array<{ name: string; values: number[]; color?: string }>;
  // 旧：仍保留演示数据（未传 series 时使用）
  data?: Array<{
    platform: string;
    day1: number;
    day2: number;
    day3: number;
    day4: number;
    day5: number;
    day6: number;
    day7: number;
  }>;
  selectedKeys?: string[];
}

/**
 * 首页「AI搜索表现分析」图表组件
 * 整合了完整的 ECharts 图表功能，支持长宽适配
 */
export const IndexPlatformVisibilityChart: React.FC<Props> = ({
  isDesktop,
  xLabels,
  series,
  data,
  selectedKeys = [
    "ChatGPT",
    "Gemini",
    "Perplexity",
    "Google AI Overviews",
    "Grok",
  ],
}) => {
  const { t } = useTranslation();

  const defaultData = [
    { platform: "ChatGPT", day1: 35, day2: 42, day3: 48, day4: 52 },
    {
      platform: "Gemini",
      day1: 60,
      day2: 64,
      day3: 67,
      day4: 50,
    },
    {
      platform: "Perplexity",
      day1: 35,
      day2: 28,
      day3: 30,
      day4: 33,
    },
    { platform: "Google AI Overviews", day1: 50, day2: 54, day3: 58, day4: 60 },
    {
      platform: "Grok",
      day1: 45,
      day2: 49,
      day3: 53,
      day4: 55,
    },
  ];

  const chartData = data || defaultData;
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstRef = useRef<echarts.ECharts | null>(null);

  // 自动tooltip演示状态
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const intervalRef = useRef<any>();

  // 获取全局Modal状态，用于暂停动画
  const { hasModalOpen } = useHeaderNav();

  // 清洗外部传入的 series，避免 undefined 造成图例或系列报错
  const safeSeries = Array.isArray(series)
    ? series
        .filter(
          (s): s is { name: string; values: number[]; color?: string } =>
            !!s && typeof s.name === "string" && Array.isArray(s.values)
        )
        .map((s) => ({
          ...s,
          values: s.values.map((v) => (Number.isFinite(v) ? v : 0)),
        }))
    : undefined;

  // 生成时间轴标签：优先使用外部传入，否则使用演示标签
  const computedXLabels = (() => {
    if (xLabels && xLabels.length) return xLabels;
    return ["3/10", "4/10", "5/10", "6/10"];
  })();

  // 根据屏幕尺寸适配图表配置
  const chartOption = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        snap: true,
        lineStyle: { type: "dashed", width: 2, color: "#6b5bd2" },
      },
      backgroundColor: "rgba(255,255,255,0.95)",
      borderColor: "#e5e7eb",
      borderWidth: 1,
      borderRadius: 6,
      shadowBlur: 10,
      shadowColor: "rgba(0,0,0,0.1)",
      textStyle: { color: "#0f172a", fontSize: isDesktop ? 12 : 10 },
      appendToBody: true, // 重要：将 tooltip 添加到 body，避免被容器遮挡
      extraCssText: "z-index: 999; box-shadow: 0 4px 12px rgba(0,0,0,0.15);", // 设置高 z-index
      hideDelay: 0, // 设置tooltip隐藏延迟为0
      enterable: true, // 允许鼠标进入tooltip
      formatter: function (params: any) {
        //let result = params[0].name + '<br/>';
        let result = "";
        params.forEach((param: any) => {
          const val =
            typeof param.value === "number" ? param.value : Number(param.value);
          const text = Number.isFinite(val) ? formatPercent(val, 1) : "--%";
          result +=
            param.marker + " " + param.seriesName + ": " + text + "<br/>";
        });
        return result;
      },
    },
    legend: {
      show: false,
      data:
        safeSeries && safeSeries.length
          ? safeSeries.map((s) => s.name)
          : ["ChatGPT", "Gemini", "Perplexity", "Google AI Overviews", "Grok"],
      top: 0,
      textStyle: {
        fontSize: isDesktop ? 11 : 9,
      },
    },
    grid: {
      left: isDesktop ? "1%" : "3%",
      right: isDesktop ? "5%" : "7%",
      bottom: isDesktop ? "5%" : "8%",
      top: isDesktop ? "2%" : "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: computedXLabels,
      show: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        show: true,
        fontSize: isDesktop ? 10 : 8,
        color: "#6b7280",
        margin: 12, // 增加一些间距
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "",
      min: 20,
      max: 80,
      axisLabel: {
        show: true,
        formatter: "{value}%",
        fontSize: isDesktop ? 10 : 8,
        color: "#6b7280",
      },
      nameTextStyle: {
        fontSize: 14,
        color: "#6b7280",
        fontWeight: "normal",
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: "#f1f5f9",
          type: "dashed",
        },
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series:
      safeSeries && safeSeries.length
        ? safeSeries.map((s, index) => {
            const color =
              s.color ||
              ["#6b5bd2", "#21C4B8", "#f59e0b", "#ef4444", "#8b5cf6"][
                index % 5
              ];
            return {
              name: s.name,
              type: "line",
              smooth: true,
              data: s.values,
              itemStyle: { color },
              lineStyle: { color, width: isDesktop ? 2 : 1.5, cap: "round" },
              symbol: "circle",
              symbolSize: isDesktop ? 6 : 4,
              emphasis: {
                focus: "series",
                itemStyle: { shadowBlur: 6, shadowColor: color },
              },
            };
          })
        : chartData.map((item, index) => {
            // 将中文/英文平台名映射为按钮的 key
            const nameToKey: Record<string, string> = {
              ChatGPT: "ChatGPT",
              Gemini: "Gemini",
              Perplexity: "Perplexity",
              AlOverview: "Google AI Overviews",
              Grok: "Grok",
            };
            const key = nameToKey[item.platform] || "";
            const selected = selectedKeys.includes(key);
            const color = [
              "#6b5bd2",
              "#21C4B8",
              "#f59e0b",
              "#ef4444",
              "#8b5cf6",
            ][index];
            return {
              name: item.platform,
              type: "line",
              smooth: true,
              data: [item.day1, item.day2, item.day3, item.day4],
              itemStyle: { color },
              lineStyle: {
                color,
                width: selected ? (isDesktop ? 3 : 2) : isDesktop ? 1.5 : 1,
                opacity: selected ? 1 : 0.3,
                cap: "round",
              },
              symbol: "circle",
              symbolSize: selected ? (isDesktop ? 10 : 6) : isDesktop ? 4 : 3,
              emphasis: {
                focus: "series",
                itemStyle: { shadowBlur: 6, shadowColor: color },
              },
              areaStyle:
                index === 1
                  ? {
                      // 给绿色系列一个淡色面积
                      color: new (echarts as any).graphic.LinearGradient(
                        0,
                        0,
                        0,
                        1,
                        [
                          { offset: 0, color: "rgba(34,197,94,0.15)" },
                          { offset: 1, color: "rgba(34,197,94,0.02)" },
                        ]
                      ),
                    }
                  : undefined,
            };
          }),
  };

  // 初始化实例
  useEffect(() => {
    if (chartRef.current && !chartInstRef.current) {
      chartInstRef.current = echarts.init(chartRef.current);
      const handleResize = () => chartInstRef.current?.resize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        chartInstRef.current?.dispose();
        chartInstRef.current = null;
      };
    }
  }, []);

  // 更新配置
  useEffect(() => {
    if (chartInstRef.current) {
      chartInstRef.current.setOption(chartOption, true);
      // 强制调整图表尺寸以适应容器
      setTimeout(() => {
        if (chartInstRef.current) {
          chartInstRef.current.resize();
        }
      }, 100);
    }
  }, [data, selectedKeys, series, xLabels, isDesktop]);

  // 自动tooltip演示逻辑
  useEffect(() => {
    let tooltipIndex = 0;
    const totalPoints = computedXLabels.length;

    const showTooltipAtIndex = (index: number) => {
      if (chartInstRef.current && !isUserInteracting && !hasModalOpen) {
        chartInstRef.current.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: index,
        });
      }
    };

    // 清除之前的定时器
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    // 只有在没有用户交互且没有Modal打开时才启动动画
    if (!isUserInteracting && !hasModalOpen && chartInstRef.current) {
      // 延迟2秒开始自动演示tooltip
      const startTimer = setTimeout(() => {
        if (!isUserInteracting && !hasModalOpen && chartInstRef.current) {
          // 首次显示tooltip（索引0）
          showTooltipAtIndex(0);
          tooltipIndex = 1;

          // 设置定时器循环显示
          intervalRef.current = setInterval(() => {
            if (!isUserInteracting && !hasModalOpen && chartInstRef.current) {
              showTooltipAtIndex(tooltipIndex);
              tooltipIndex = (tooltipIndex + 1) % totalPoints;
            }
          }, 2500);
        }
      }, 2000);

      return () => {
        clearTimeout(startTimer);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = undefined;
        }
      };
    }

    // 当Modal打开或用户交互时，隐藏当前tooltip
    if ((hasModalOpen || isUserInteracting) && chartInstRef.current) {
      chartInstRef.current.dispatchAction({
        type: "hideTip",
      });
    }
  }, [isUserInteracting, hasModalOpen]);

  // 监听用户交互
  useEffect(() => {
    if (chartInstRef.current) {
      const handleMouseOver = () => {
        setIsUserInteracting(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };

      const handleMouseOut = () => {
        // 3秒后恢复自动演示
        setTimeout(() => {
          setIsUserInteracting(false);
        }, 3000);
      };

      // 监听图表的鼠标事件
      chartInstRef.current.on("mouseover", handleMouseOver);
      chartInstRef.current.on("mouseout", handleMouseOut);

      return () => {
        if (chartInstRef.current) {
          chartInstRef.current.off("mouseover", handleMouseOver);
          chartInstRef.current.off("mouseout", handleMouseOut);
        }
      };
    }
  }, []);

  return (
    <div
      style={{
        height: isDesktop ? "300px" : "240px",
        background: "#ffffff",
        position: "relative",
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
        borderRadius: "6px 6px 0 0",
        zIndex: 1, // 确保容器有合适的层级
        padding: "10px",
      }}
    >
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height: "100%",
          background: "#ffffff",
          borderRadius: "6px 6px 0 0",
          position: "relative",
          zIndex: 2, // 图表容器层级
        }}
      />
    </div>
  );
};

export default IndexPlatformVisibilityChart;
